// Public-shape API client for the OnboardConnect self-service portal.
// All endpoints live under /api/selfservice/:slug on the public worker.

const OC_API_BASE: string =
  (import.meta.env.VITE_OC_SELFSERVICE_API_BASE as string | undefined) ?? "";

// ─── Response shapes ────────────────────────────────────────────────────────

export interface OcPortalConfig {
  tenantName: string;
  branding: {
    logoUrl?: string;
    primaryColor?: string;
    supportContactText?: string;
  };
  allowedChannels: {
    email: boolean;
    sms: boolean;
  };
  turnstileSiteKey: string;
  supportText: string | null;
  /** Requirements sentence derived from the tenant's provisioning password policy. */
  passwordHint?: string | null;
}

export interface OcAllowedChannelTarget {
  channel: "email" | "sms";
  target: string; // already masked by the worker
}

// All endpoints return a flat object. On success the body matches the typed
// response shape; on failure the worker emits { error, message? } with the
// matching HTTP status. Step components discriminate by checking `result.error`
// — that field is absent on success, so falsy-checking it is the success path.
export interface OcLookupResponse {
  ok?: boolean;
  sessionId?: string | null;
  allowedChannels?: OcAllowedChannelTarget[];
  error?: string;
  message?: string;
}

export interface OcSimpleOkResponse {
  ok?: boolean;
  error?: string;
  message?: string;
}

// ─── Core fetcher ───────────────────────────────────────────────────────────

async function ocPortalFetch<T extends { error?: string }>(
  slug: string,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const url = `${OC_API_BASE}/api/selfservice/${encodeURIComponent(slug)}${path}`;
  let response: Response;
  try {
    response = await fetch(url, init);
  } catch {
    return { error: "network_error" } as T;
  }

  const text = await response.text();
  if (!text) {
    return (response.ok ? {} : { error: "empty_response" }) as T;
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    return { error: response.ok ? "invalid_response" : "server_error" } as T;
  }
}

function ocJsonInit(body: unknown): RequestInit {
  return {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  };
}

// ─── Public named exports ───────────────────────────────────────────────────

export type OcPortalConfigResponse = OcPortalConfig & { error?: string; message?: string };

export function ocFetchConfig(slug: string): Promise<OcPortalConfigResponse> {
  return ocPortalFetch<OcPortalConfigResponse>(slug, "/config", { method: "GET" });
}

export interface OcLookupBody {
  identifier: string;
  turnstileToken: string;
}
export function ocLookup(slug: string, body: OcLookupBody): Promise<OcLookupResponse> {
  return ocPortalFetch<OcLookupResponse>(slug, "/lookup", ocJsonInit(body));
}

export interface OcSendCodeBody {
  sessionId: string;
  channel: "email" | "sms";
}
export function ocSendCode(slug: string, body: OcSendCodeBody): Promise<OcSimpleOkResponse> {
  return ocPortalFetch<OcSimpleOkResponse>(slug, "/send-code", ocJsonInit(body));
}

export interface OcVerifyBody {
  sessionId: string;
  code: string;
}
export function ocVerify(slug: string, body: OcVerifyBody): Promise<OcSimpleOkResponse> {
  return ocPortalFetch<OcSimpleOkResponse>(slug, "/verify", ocJsonInit(body));
}

export interface OcResetBody {
  sessionId: string;
  newPassword: string;
}
export function ocReset(slug: string, body: OcResetBody): Promise<OcSimpleOkResponse> {
  return ocPortalFetch<OcSimpleOkResponse>(slug, "/reset", ocJsonInit(body));
}
